import React, { useEffect, useState } from 'react';
import { RnRadioGroup } from '@app/react-baseweb/baseweb-custom-components/rn-baseweb-radio';
import { useStyletron } from 'styletron-react';
import { useForm } from 'react-hook-form';
import { Textarea } from 'baseui/textarea';
import {
  ConfigurationFooter,
  InputTitle,
} from '@app/api-mgmt/components/push-api/styles/ConfigurationInputStyles';
import { SfdcConfiguration } from '@app/api-mgmt/components/push-api/types/push-api-models';
import { PushAPIService } from '@app/api-mgmt/components/push-api/push-api-service';
import { useClientInfo } from '@app/api-mgmt/components/push-api/context/ClientInfoContext';
import {
  validateName,
  validateMyDomainUrl,
  validateSaleforceConsumerKey,
  validateSaleforceUsername,
} from '@app/api-mgmt/components/push-api/functions/validate';
import { Redirect } from '@app/api-mgmt/components/push-api/components/configurations-display/Redirect';
import { InputBox } from '@app/api-mgmt/components/push-api/components/configurations-display/InputBox';
import { Button } from 'baseui/button';
import {
  ButtonCancelOverride,
  ButtonOverride,
  TextAreaOverride,
} from '@app/react-baseweb/baseweb-custom-theme/customTheme';
import { ButtonIconRight } from '@app/api-mgmt/components/push-api/styles/AddConfigurationButtonStyles';
import { CertificateDownload } from '@app/api-mgmt/components/push-api/components/certificate-modal/CertificateDownload';
import { SelectBox } from '@app/api-mgmt/components/push-api/components/configurations-display/SelectBox';
import { Options, Value } from 'baseui/select';
import { Checkbox } from 'baseui/checkbox';
import { StatefulTooltip } from 'baseui/tooltip';
import {
  CheckboxCheckedOverride,
  CheckboxUncheckedOverride,
  CheckboxDisabledOverride,
} from '@app/react-baseweb/baseweb-custom-theme/customTheme';
import { useFeatureFlags } from '@app/api-mgmt/components/push-api/context/FeatureFlagsContext';

const MIN_ENCRYPTION_VERSION = '1.31';

const isVersionAtLeast = (version: string, min: string) => {
  const toParts = (v: string) => v.split('.').map(Number);
  const versionParts = toParts(version);
  const minParts = toParts(min);
  const length = Math.max(versionParts.length, minParts.length);

  for (let i = 0; i < length; i++) {
    const versionPart = versionParts[i] ?? 0;
    const minPart = minParts[i] ?? 0;
    if (versionPart !== minPart) {
      return versionPart > minPart;
    }
  }
  return true;
};

type SfdcFormDataBase = {
  salesforce_version?: any;
  name: string;
  uid: string;
  active: boolean;
  queue_name: string;
  salesforce_username: string;
  salesforce_consumer_key: string;
};

export type SfdcFormData = SfdcFormDataBase & (
  | { salesforce_subdomain: 'login' | 'test'; salesforce_my_domain_url?: string }
  | { salesforce_subdomain: 'my_domain'; salesforce_my_domain_url: string }
);

export const SfdcEditor = ({
  onSaveClicked,
  onCancelClicked,
  integration,
}: {
  onSaveClicked?: (newIntegration: SfdcConfiguration) => Promise<void>;
  onCancelClicked?: () => void;
  integration?: SfdcConfiguration;
}) => {
  const [css] = useStyletron();
  const [{ clientId }] = useClientInfo();
  const featureFlags = useFeatureFlags();
  const isEventEncryptionEnabled =
    featureFlags.find(
      (flag) => flag.flagName === 'push_api_sfdc_event_encryption',
    )?.flagValue ?? false;

  const defaultFormData = {
    uid: integration?.uid ?? '',
    versionValue: integration?.salesforce_version ?
      [{ label: integration?.salesforce_version, id: integration?.salesforce_version }] : [],
    name: integration?.name ?? '',
    active: integration?.active ?? true,
    queue_name: integration?.queue_name ?? '',
    salesforce_username: integration?.salesforce_username ?? '',
    salesforce_subdomain: integration?.salesforce_subdomain ?? 'test',
    salesforce_my_domain_url: integration?.salesforce_my_domain_url ?? '',
    salesforce_consumer_key: integration?.salesforce_consumer_key ?? '',
    public_key: integration?.public_key ?? '',
    newapi: (integration?.newapi !== undefined) ? integration.newapi : true,
    encryptionEnabled: integration?.encryption?.enabled ?? false,
  };

  const [versionList, setVersionList] = React.useState<Options>(null);
  const [versionValue, setVersionValue] = React.useState<Value>(
    defaultFormData.versionValue,
  );
  const [name, setName] = useState(defaultFormData.name);
  const [username, setUsername] = useState(defaultFormData.salesforce_username);
  const [subdomain, setSubdomain] = useState<'test' | 'login' | 'my_domain'>(
    defaultFormData.salesforce_subdomain,
  );
  const [myDomainUrl, setMyDomainUrl] = useState(
    defaultFormData.salesforce_my_domain_url,
  );
  const [consumerKey, setConsumerKeys] = useState(
    defaultFormData.salesforce_consumer_key,
  );

  // some values are dependant on the form input values. Those are initalized here
  // and linked to the form input data.
  const [isValid, setIsValid] = useState(false);

  const [newapi, setNewapi] = useState(defaultFormData.newapi);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittingType, setSubmittingType] = useState<'activate' | 'save' | undefined>(undefined);

  const [encryptionEnabled, setEncryptionEnabled] = useState(
    defaultFormData.encryptionEnabled,
  );

  const selectedVersion = versionValue[0]?.id as string | undefined;
  const isEncryptionEligibleVersion =
    !!selectedVersion && isVersionAtLeast(selectedVersion, MIN_ENCRYPTION_VERSION);
  const isEncryptionCheckboxEnabled =
    isEncryptionEligibleVersion && isEventEncryptionEnabled;

  // Reset only on version-ineligibility, not on the feature flag being off:
  // the flag can be off simply because this admin's client isn't targeted,
  // and must not silently clear encryption a previous save already persisted.
  useEffect(() => {
    if (!isEncryptionEligibleVersion && encryptionEnabled) {
      setEncryptionEnabled(false);
    }
  }, [isEncryptionEligibleVersion]);

  useEffect(() => {
    const myDomainValid =
      subdomain !== 'my_domain' || validateMyDomainUrl(myDomainUrl) === '';
    setIsValid(
      validateName(name, integration?.name, integrationNames) === '' &&
        validateSaleforceUsername(username) === '' &&
        validateSaleforceConsumerKey(consumerKey) === '' &&
        myDomainValid,
    );
  }, [name, username, subdomain, myDomainUrl, consumerKey]);

  // some values need to be pulled from the backend, so they're initialized here
  const [sfdcEventList, setSfdcEventList] = React.useState<string[]>([]);
  const [integrationNames, setIntegrationNames] = useState([]);
  useEffect(() => {
    PushAPIService.getEventSubscriptionList('sfdc').then((fetchedEvents) => {
      setSfdcEventList(fetchedEvents);
    });
    PushAPIService.getIntegrationNames(clientId).then((integrationNames) => {
      setIntegrationNames(integrationNames);
    });
  }, []);

  const { reset } = useForm<SfdcFormData>({
    defaultValues: defaultFormData,
  });

  /**
   * Clears the form data
   */
  const cancel = () => {
    // reset data that was entered when modal closes
    reset(defaultFormData);
    // callback to close the modal
    onCancelClicked();
  };

  const checkIfActive = () => {
    if (integration && integration.active === true) {
      return true;
    }
  };

  /**
   * Handles when data is submitted to the form (e.g. click Save or press
   * Enter).
   */
  const onSubmit = async ({ activate }: { activate: boolean }) => {
    if (isSubmitting) return;

    // There's no UI yet to supply the required encryption key (publicKeyPem)
    // — the backend rejects `encryption.enabled: true` without one. Catch
    // this client-side with a clear, deterministic message instead of
    // sending a request that's guaranteed to fail with a confusing
    // backend validation error (a future release will add key upload and
    // this check goes away).
    if (isEventEncryptionEnabled && encryptionEnabled) {
      alert(
        'Event Encryption requires an encryption key, which cannot be ' +
          'provided in this UI yet. Uncheck "Event Encryption Enabled" to ' +
          'save without it — key upload support is coming in a future release.',
      );
      return;
    }

    try {
      if (isValid) {
        setIsSubmitting(true);
        setSubmittingType(activate ? 'activate' : 'save');

        const submission = {
          uid: integration?.uid ?? '',
          queue_name: integration?.queue_name ?? '',
          public_key: integration?.public_key ?? '',
          salesforce_version: versionValue[0]?.id as string,
          name,
          salesforce_username: username,
          salesforce_subdomain: subdomain,
          ...(subdomain === 'my_domain' ? { salesforce_my_domain_url: myDomainUrl } : {}),
          salesforce_consumer_key: consumerKey,
          type: 'sfdc',
          active: activate,
          newapi,
          // Only send `encryption` when the flag is on AND the checkbox is
          // actually checked — both conditions matter:
          //  - Checkbox must be checked: the backend requires `publicKeyPem`
          //    whenever `encryption` is present at all (regardless of
          //    `enabled`'s value), and this UI has no key-input field yet —
          //    sending it unconditionally would break every save, not just
          //    ones that touch encryption.
          //  - Flag must be on: when the flag is off, `encryptionEnabled`
          //    can still be `true` from a previously-saved integration (the
          //    checkbox UI is just hidden, not reset) — omitting the key
          //    here lets the backend's merge-on-update preserve whatever was
          //    already saved instead of us resending/overwriting it.
          ...(isEventEncryptionEnabled && encryptionEnabled
            ? { encryption: { enabled: encryptionEnabled } }
            : {}),
        };
        await onSaveClicked?.(submission as SfdcConfiguration);
      }
    } finally {
      setIsSubmitting(false);
      setSubmittingType(undefined);
    }
  };

const isExistingIntegration = integration?.uid && integration?.uid !== '';

const formatVersionOptions = (versions: Record<string, string>) => {
    let versionOptions = Object.keys(versions)
      .map((version) => ({
        label: `${version} - ${versions[version]}`,
        id: version,
      }));

    setVersionList(versionOptions as Options);

    if (isExistingIntegration && (!integration.salesforce_version || integration.salesforce_version === '')) {
      //if this is an existing integration that was configured prior to versioning, don't set a version here so that the placeholder text will be displayed.
      return;
    }

    if (!versionValue || versionValue.length === 0) {
      if (integration?.salesforce_version) {
        setVersionValue([
          {
            label: integration.salesforce_version,
            id: integration.salesforce_version,
          },
        ]);
      } else {
        setVersionValue([versionOptions[0]]);
      }
    }
  };

  async function fetchVersions() {
    const fetchedVersions = await PushAPIService.getSfdcVersions('false');
    formatVersionOptions(fetchedVersions);
  }

  useEffect(() => {
    fetchVersions();
  }, []);

  return (
    <div
      className={css({
        margin: 'auto',
      })}
    >
      <Redirect
        link="https://docs.relaynetwork.com/docs/salesforcecom"
        value="View Relay Integration Documentation"
      />
      <SelectBox
        placeholder="Not Configured (1.2 - Minimum supported package version)"
        title="Version"
        options={versionList}
        value={versionValue}
        onChange={(value) => setVersionValue(value)}
      />
      {isEventEncryptionEnabled && (
        <div style={{ margin: '10px 10px 0px 0px' }}>
          <Checkbox
            key={'encryptionEnabled'}
            checked={encryptionEnabled}
            disabled={!isEncryptionCheckboxEnabled}
            overrides={
              !isEncryptionCheckboxEnabled
                ? { Checkmark: CheckboxDisabledOverride }
                : encryptionEnabled
                ? { Checkmark: CheckboxCheckedOverride }
                : { Checkmark: CheckboxUncheckedOverride }
            }
            onChange={() => setEncryptionEnabled(!encryptionEnabled)}
          >
            <div>
              <span
                style={{
                  color: '#5b5a59',
                }}
              >
                Event Encryption Enabled
              </span>
              <StatefulTooltip
                accessibilityType={'tooltip'}
                content={'Encrypts event payloads sent to the RelayEncryptedPushData__c object. Requires package version 1.31 or later.'}
                showArrow
                placement="top"
              >
                <i
                  className="fa fa-question-circle"
                  style={{
                    fontSize: '16px',
                    marginLeft: '10px',
                    color: '#908E8F',
                  }}
                ></i>
              </StatefulTooltip>
            </div>
          </Checkbox>
        </div>
      )}
      <InputBox
        title="Name"
        value={name}
        placeholder="Integration Name"
        validate={(value) =>
          validateName(value, integration.name, integrationNames)
        }
        onChange={setName}
        preValidate={isExistingIntegration}
      />
      <InputBox
        placeholder=""
        title="Salesforce Username"
        subtitle="sub"
        value={username}
        validate={validateSaleforceUsername}
        onChange={setUsername}
        preValidate={isExistingIntegration}
      />
      <InputTitle>
        <label>Salesforce Subdomain</label>
        <span style={{ fontSize: '16px' }}> (aud)</span>
      </InputTitle>

      <RnRadioGroup
        value={subdomain}
        options={[
          { value: 'test', label: 'Test' },
          { value: 'login', label: 'Login' },
          { value: 'my_domain', label: 'My Domain' },
        ]}
        onChange={(e) => setSubdomain(e)}
      ></RnRadioGroup>
      {subdomain === 'my_domain' && (
        <InputBox
          placeholder="https://mycompany.my.salesforce.com"
          title="My Domain URL"
          value={myDomainUrl}
          validate={validateMyDomainUrl}
          onChange={setMyDomainUrl}
          preValidate={isExistingIntegration}
        />
      )}
      <InputBox
        placeholder=""
        title="Salesforce Consumer Key"
        subtitle="iss"
        value={consumerKey}
        validate={validateSaleforceConsumerKey}
        onChange={setConsumerKeys}
        preValidate={isExistingIntegration}
      />
      <InputTitle>
        <label>Events</label>
      </InputTitle>

      <Textarea
        overrides={{
          InputContainer: {
            style: {
              ...TextAreaOverride.style,
              height: '360px',
            },
          },
        }}
        value={sfdcEventList.join('\n')}
        disabled={true}
      />
      {defaultFormData.public_key && (
        <CertificateDownload
          publicKey={defaultFormData.public_key}
          integrationName={defaultFormData.name}
          clientId={clientId}
        />
      )}
      <ConfigurationFooter>
        <div style={{ float: 'left' }}>
          <Button
            type="reset"
            kind="tertiary"
            onClick={cancel}
            overrides={{ Root: ButtonCancelOverride }}
          >
            Cancel
          </Button>
        </div>
        <div style={{ float: 'right', opacity: isValid ? '100%' : '50%' }}>
          <Button
            onClick={() => onSubmit({ activate: false })}
            kind="tertiary"
            overrides={{ Root: ButtonCancelOverride }}
            disabled={isSubmitting && submittingType === 'save'}
          >
            {isSubmitting && submittingType === 'save'
              ? checkIfActive()
                ? 'Deactivating...'
                : 'Saving...'
              : checkIfActive()
              ? 'Save & Deactivate'
              : 'Save & Exit'}
          </Button>
          <Button
            onClick={() => onSubmit({ activate: true })}
            overrides={{
              BaseButton: ButtonOverride,
            }}
            disabled={isSubmitting && submittingType === 'activate'}
          >
            <span>
              {isSubmitting && submittingType === 'activate'
                ? 'Activating...'
                : checkIfActive()
                ? 'SAVE'
                : 'ACTIVATE'}
            </span>
            <ButtonIconRight>
              <i className="fa-solid fa-check"></i>
            </ButtonIconRight>
          </Button>
        </div>
      </ConfigurationFooter>
    </div>
  );
};
